import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SystemSettingsTab = () => {
  const [settings, setSettings] = useState({
    vat: {
      defaultRate: '20',
      selfLiquidationEnabled: true,
      rate10Enabled: true,
      rate20Enabled: true
    },
    email: {
      senderName: 'KitchenQuote Pro',
      senderEmail: 'devis@kitchenquote.fr',
      subjectTemplate: 'Votre devis cuisine - {clientName}',
      bodyTemplate: `Bonjour {clientName},

Veuillez trouver ci-joint votre devis personnalisé pour votre projet de cuisine.

Ce devis est valable 30 jours à compter de la date d'émission.Pour toute question, n'hésitez pas à nous contacter.

Cordialement,
L'équipe KitchenQuote Pro`,
      autoSend: false,
      copyToAdmin: true
    },
    pdf: {
      companyName: 'KitchenQuote Pro',companyAddress: `123 Rue de la Cuisine75001 Paris, France`,companyPhone: '+33 1 23 45 67 89',companyEmail: 'contact@kitchenquote.fr',logoUrl: '',footerText: 'Devis généré automatiquement par KitchenQuote Pro',
      showPriceDetails: true,
      showClientSignature: true,
      watermark: false
    },
    system: {
      quoteValidityDays: 30,
      autoBackup: true,
      backupFrequency: 'daily',
      maintenanceMode: false,
      debugMode: false
    },
    users: {
      adminPassword: '',confirmPassword: '',
      requirePasswordChange: false,
      sessionTimeout: 30
    }
  });

  const [activeSection, setActiveSection] = useState('vat');
  const [hasChanges, setHasChanges] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});

  const vatOptions = [
    { value: '0', label: 'Auto-liquidation (0%)' },
    { value: '10', label: 'TVA réduite (10%)' },
    { value: '20', label: 'TVA normale (20%)' }
  ];

  const backupFrequencyOptions = [
    { value: 'hourly', label: 'Toutes les heures' },
    { value: 'daily', label: 'Quotidienne' },
    { value: 'weekly', label: 'Hebdomadaire' }
  ];

  const sectionConfig = {
    vat: {
      title: 'Configuration TVA',
      description: 'Paramètres de taxation et taux de TVA',
      icon: 'Calculator'
    },
    email: {
      title: 'Templates email',
      description: 'Personnalisation des emails de devis',
      icon: 'Mail'
    },
    pdf: {
      title: 'Format PDF',
      description: 'Mise en forme des documents PDF',
      icon: 'FileText'
    },
    system: {
      title: 'Paramètres système',
      description: 'Configuration générale de l\'application',
      icon: 'Settings'
    },
    users: {
      title: 'Gestion utilisateurs',
      description: 'Modification des mots de passe et paramètres utilisateurs',
      icon: 'Users'
    }
  };

  const handleSettingChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev?.[section],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const validatePassword = () => {
    const errors = {};
    const password = settings?.users?.adminPassword;
    const confirmPassword = settings?.users?.confirmPassword;

    if (password && password?.length < 8) {
      errors.adminPassword = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (password && !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(password)) {
      errors.adminPassword = 'Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre';
    }

    if (password && confirmPassword && password !== confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setPasswordErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handlePasswordChange = () => {
    if (!validatePassword()) {
      return;
    }

    if (window.confirm('Êtes-vous sûr de vouloir modifier le mot de passe administrateur ?')) {
      // Handle password change logic here
      console.log('Changing admin password');
      alert('Mot de passe administrateur modifié avec succès');
      
      // Clear password fields after successful change
      setSettings(prev => ({
        ...prev,
        users: {
          ...prev?.users,
          adminPassword: '',
          confirmPassword: ''
        }
      }));
      setPasswordErrors({});
    }
  };

  const handleSaveSettings = () => {
    // Save settings logic here
    console.log('Saving settings:', settings);
    setHasChanges(false);
    alert('Paramètres sauvegardés avec succès');
  };

  const handleResetSettings = () => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser tous les paramètres ?')) {
      // Reset to default values
      setHasChanges(false);
      alert('Paramètres réinitialisés');
    }
  };

  const renderVATSection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select
          label="Taux de TVA par défaut"
          options={vatOptions}
          value={settings?.vat?.defaultRate}
          onChange={(value) => handleSettingChange('vat', 'defaultRate', value)}
        />
      </div>

      <div className="space-y-4">
        <h4 className="font-medium text-text-primary">Options de TVA disponibles</h4>
        <div className="space-y-3">
          <Checkbox
            label="Auto-liquidation (0%)"
            description="Permettre l'option d'auto-liquidation de TVA"
            checked={settings?.vat?.selfLiquidationEnabled}
            onChange={(e) => handleSettingChange('vat', 'selfLiquidationEnabled', e?.target?.checked)}
          />
          <Checkbox
            label="TVA réduite (10%)"
            description="Activer le taux de TVA réduit à 10%"
            checked={settings?.vat?.rate10Enabled}
            onChange={(e) => handleSettingChange('vat', 'rate10Enabled', e?.target?.checked)}
          />
          <Checkbox
            label="TVA normale (20%)"
            description="Activer le taux de TVA normal à 20%"
            checked={settings?.vat?.rate20Enabled}
            onChange={(e) => handleSettingChange('vat', 'rate20Enabled', e?.target?.checked)}
          />
        </div>
      </div>
    </div>
  );

  const renderEmailSection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Nom expéditeur"
          value={settings?.email?.senderName}
          onChange={(e) => handleSettingChange('email', 'senderName', e?.target?.value)}
          placeholder="Nom de votre entreprise"
        />
        <Input
          label="Email expéditeur"
          type="email"
          value={settings?.email?.senderEmail}
          onChange={(e) => handleSettingChange('email', 'senderEmail', e?.target?.value)}
          placeholder="contact@votre-entreprise.fr"
        />
      </div>

      <Input
        label="Objet du message"
        value={settings?.email?.subjectTemplate}
        onChange={(e) => handleSettingChange('email', 'subjectTemplate', e?.target?.value)}
        description="Utilisez {clientName} pour insérer le nom du client"
      />

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Corps du message
        </label>
        <textarea
          value={settings?.email?.bodyTemplate}
          onChange={(e) => handleSettingChange('email', 'bodyTemplate', e?.target?.value)}
          rows={8}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
          placeholder="Contenu de l'email..."
        />
        <p className="text-xs text-text-secondary mt-1">
          Variables disponibles: {'{clientName}'}, {'{quoteNumber}'}, {'{date}'}
        </p>
      </div>

      <div className="space-y-3">
        <Checkbox
          label="Envoi automatique"
          description="Envoyer automatiquement les devis par email"
          checked={settings?.email?.autoSend}
          onChange={(e) => handleSettingChange('email', 'autoSend', e?.target?.checked)}
        />
        <Checkbox
          label="Copie à l'administrateur"
          description="Envoyer une copie de chaque devis à l'administrateur"
          checked={settings?.email?.copyToAdmin}
          onChange={(e) => handleSettingChange('email', 'copyToAdmin', e?.target?.checked)}
        />
      </div>
    </div>
  );

  const renderPDFSection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Nom de l'entreprise"
          value={settings?.pdf?.companyName}
          onChange={(e) => handleSettingChange('pdf', 'companyName', e?.target?.value)}
        />
        <Input
          label="Téléphone"
          value={settings?.pdf?.companyPhone}
          onChange={(e) => handleSettingChange('pdf', 'companyPhone', e?.target?.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Adresse de l'entreprise
        </label>
        <textarea
          value={settings?.pdf?.companyAddress}
          onChange={(e) => handleSettingChange('pdf', 'companyAddress', e?.target?.value)}
          rows={3}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Email de contact"
          type="email"
          value={settings?.pdf?.companyEmail}
          onChange={(e) => handleSettingChange('pdf', 'companyEmail', e?.target?.value)}
        />
        <Input
          label="URL du logo"
          value={settings?.pdf?.logoUrl}
          onChange={(e) => handleSettingChange('pdf', 'logoUrl', e?.target?.value)}
          placeholder="https://..."
        />
      </div>

      <Input
        label="Texte de pied de page"
        value={settings?.pdf?.footerText}
        onChange={(e) => handleSettingChange('pdf', 'footerText', e?.target?.value)}
      />

      <div className="space-y-3">
        <Checkbox
          label="Afficher le détail des prix"
          description="Inclure le détail de chaque élément dans le PDF"
          checked={settings?.pdf?.showPriceDetails}
          onChange={(e) => handleSettingChange('pdf', 'showPriceDetails', e?.target?.checked)}
        />
        <Checkbox
          label="Zone de signature client"
          description="Ajouter un espace pour la signature du client"
          checked={settings?.pdf?.showClientSignature}
          onChange={(e) => handleSettingChange('pdf', 'showClientSignature', e?.target?.checked)}
        />
        <Checkbox
          label="Filigrane"
          description="Ajouter un filigrane sur les documents"
          checked={settings?.pdf?.watermark}
          onChange={(e) => handleSettingChange('pdf', 'watermark', e?.target?.checked)}
        />
      </div>
    </div>
  );

  const renderSystemSection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Validité des devis (jours)"
          type="number"
          value={settings?.system?.quoteValidityDays}
          onChange={(e) => handleSettingChange('system', 'quoteValidityDays', parseInt(e?.target?.value))}
          min="1"
          max="365"
        />
        <Select
          label="Fréquence de sauvegarde"
          options={backupFrequencyOptions}
          value={settings?.system?.backupFrequency}
          onChange={(value) => handleSettingChange('system', 'backupFrequency', value)}
        />
      </div>

      <div className="space-y-3">
        <Checkbox
          label="Sauvegarde automatique"
          description="Effectuer des sauvegardes automatiques des données"
          checked={settings?.system?.autoBackup}
          onChange={(e) => handleSettingChange('system', 'autoBackup', e?.target?.checked)}
        />
        <Checkbox
          label="Mode maintenance"
          description="Activer le mode maintenance (accès limité)"
          checked={settings?.system?.maintenanceMode}
          onChange={(e) => handleSettingChange('system', 'maintenanceMode', e?.target?.checked)}
        />
        <Checkbox
          label="Mode debug"
          description="Activer les logs détaillés pour le débogage"
          checked={settings?.system?.debugMode}
          onChange={(e) => handleSettingChange('system', 'debugMode', e?.target?.checked)}
        />
      </div>

      <div className="bg-muted p-4 rounded-lg">
        <h4 className="font-medium text-text-primary mb-2">Actions système</h4>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" iconName="Download">
            Exporter configuration
          </Button>
          <Button variant="outline" iconName="Upload">
            Importer configuration
          </Button>
          <Button variant="outline" iconName="Database">
            Sauvegarder données
          </Button>
          <Button variant="destructive" iconName="RotateCcw">
            Réinitialiser
          </Button>
        </div>
      </div>
    </div>
  );

  const renderUsersSection = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-800 mb-1">Important</h4>
            <p className="text-sm text-yellow-700">
              La modification du mot de passe administrateur affectera l'accès à toutes les fonctionnalités administratives.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="relative">
          <Input
            label="Nouveau mot de passe administrateur"
            type={showPassword ? "text" : "password"}
            value={settings?.users?.adminPassword}
            onChange={(e) => handleSettingChange('users', 'adminPassword', e?.target?.value)}
            onBlur={validatePassword}
            placeholder="Entrez le nouveau mot de passe"
            error={passwordErrors?.adminPassword}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-8 text-text-secondary hover:text-text-primary"
          >
            <Icon name={showPassword ? "EyeOff" : "Eye"} size={16} />
          </button>
        </div>

        <div className="relative">
          <Input
            label="Confirmer le mot de passe"
            type={showConfirmPassword ? "text" : "password"}
            value={settings?.users?.confirmPassword}
            onChange={(e) => handleSettingChange('users', 'confirmPassword', e?.target?.value)}
            onBlur={validatePassword}
            placeholder="Confirmez le mot de passe"
            error={passwordErrors?.confirmPassword}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-8 text-text-secondary hover:text-text-primary"
          >
            <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={16} />
          </button>
        </div>
      </div>

      <div className="text-xs text-text-secondary space-y-1">
        <p>• Le mot de passe doit contenir au moins 8 caractères</p>
        <p>• Au moins une majuscule, une minuscule et un chiffre</p>
        <p>• Évitez d'utiliser des informations personnelles</p>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="space-y-3">
          <Checkbox
            label="Forcer le changement de mot de passe"
            description="Obliger les utilisateurs à changer leur mot de passe lors de la prochaine connexion"
            checked={settings?.users?.requirePasswordChange}
            onChange={(e) => handleSettingChange('users', 'requirePasswordChange', e?.target?.checked)}
          />
        </div>

        <Button
          variant="primary"
          iconName="Key"
          onClick={handlePasswordChange}
          disabled={!settings?.users?.adminPassword || !settings?.users?.confirmPassword}
        >
          Modifier le mot de passe
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-border">
        <Input
          label="Délai d'expiration de session (minutes)"
          type="number"
          value={settings?.users?.sessionTimeout}
          onChange={(e) => handleSettingChange('users', 'sessionTimeout', parseInt(e?.target?.value))}
          min="5"
          max="480"
          placeholder="30"
        />
      </div>

      <div className="bg-muted p-4 rounded-lg">
        <h4 className="font-medium text-text-primary mb-2">Actions utilisateurs</h4>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" iconName="RefreshCw">
            Réinitialiser sessions
          </Button>
          <Button variant="outline" iconName="Download">
            Exporter logs connexion
          </Button>
          <Button variant="outline" iconName="Shield">
            Vérifier sécurité
          </Button>
        </div>
      </div>
    </div>
  );

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'vat':
        return renderVATSection();
      case 'email':
        return renderEmailSection();
      case 'pdf':
        return renderPDFSection();
      case 'system':
        return renderSystemSection();
      case 'users':
        return renderUsersSection();
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Section Selector */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Paramètres système</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {Object.entries(sectionConfig)?.map(([key, section]) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className={`p-4 rounded-lg border transition-smooth text-left ${
                activeSection === key
                  ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/50 text-text-secondary hover:text-text-primary'
              }`}
            >
              <div className="flex items-center space-x-3 mb-2">
                <Icon name={section?.icon} size={20} />
                <span className="font-medium">{section?.title}</span>
              </div>
              <p className="text-sm opacity-80">{section?.description}</p>
            </button>
          ))}
        </div>
      </div>
      {/* Settings Content */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Icon name={sectionConfig?.[activeSection]?.icon} size={24} className="text-primary" />
            <div>
              <h3 className="text-lg font-semibold text-text-primary">
                {sectionConfig?.[activeSection]?.title}
              </h3>
              <p className="text-sm text-text-secondary">
                {sectionConfig?.[activeSection]?.description}
              </p>
            </div>
          </div>
          
          {hasChanges && activeSection !== 'users' && (
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={handleResetSettings}
              >
                Annuler
              </Button>
              <Button
                variant="default"
                iconName="Save"
                onClick={handleSaveSettings}
              >
                Sauvegarder
              </Button>
            </div>
          )}
        </div>

        {renderSectionContent()}
      </div>
    </div>
  );
};

export default SystemSettingsTab;